export interface PushSubscriptionInfo {
  endpoint: string;
  publicKey: string;
  auth: string;
}

export function isNotificationSupported(): boolean {
  let supported = true
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("showNotification" in ServiceWorkerRegistration.prototype)
  ) {
    supported = false
  }
  return supported
}

export async function registerAndSubscribe(onSubscribe: (subscriptionInfo: PushSubscriptionInfo) => void,
                                           onError: (e: Error) => void,
                                           vapidPublicKey: string): Promise<boolean> {
  if (!isNotificationSupported()) {
    console.error("Service workers are not supported in this browser.")
    return true
  }

  try {
    if (!vapidPublicKey) {
      console.warn("No vapid public key provided.")
      return false
    }

    return await handleSubscription(Notification.permission, onSubscribe, vapidPublicKey)
  } catch (error: any) {
    console.error("Push subscription failed:", error)
    onError(error)
    return false
  }
}

async function handleSubscriptionForGrantedPermission(onSubscribe: (subscriptionInfo: PushSubscriptionInfo) => void,
                                                      vapidPublicKey: string): Promise<boolean> {
  const registration = await navigator.serviceWorker.ready
  const prevSubscription = await registration.pushManager.getSubscription()
  const subscription = prevSubscription ?? await subscribe(registration, vapidPublicKey)
  onSubscribe(toSubscriptionInfo(subscription))
  return true
}

async function handleSubscriptionForDeniedPermission(): Promise<boolean> {
  console.warn("Notification permission denied.")
  return true
}

async function handleSubscriptionForDefaultPermission(onSubscribe: (subscriptionInfo: PushSubscriptionInfo) => void,
                                                      vapidPublicKey: string): Promise<boolean> {
  const permission = await Notification.requestPermission()
  return handleSubscription(permission, onSubscribe, vapidPublicKey)
}

async function handleSubscription(permission: NotificationPermission,
                                  onSubscribe: (subscriptionInfo: PushSubscriptionInfo) => void,
                                  vapidPublicKey: string): Promise<boolean> {
  console.debug("handling subscription with permission: ", permission)

  switch (permission) {
    case "granted":
      return handleSubscriptionForGrantedPermission(onSubscribe, vapidPublicKey)
    case "denied":
      return handleSubscriptionForDeniedPermission()
    default:
      return handleSubscriptionForDefaultPermission(onSubscribe, vapidPublicKey)
  }
}

async function subscribe(registration: ServiceWorkerRegistration, vapidPublicKey: string): Promise<PushSubscription> {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  })
}
function toSubscriptionInfo(subscription: PushSubscription): PushSubscriptionInfo {
  return {
    endpoint: subscription.endpoint,
    publicKey: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("p256dh")!))),
    auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("auth")!)))
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  return new Uint8Array(Array.from(rawData, (char) => char.charCodeAt(0)))
}
